import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, RotateCcw, Eye, FileDown, Upload, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader, EmptyState, StatusBadge, formatDate } from "../ui";
import { formatCpfCnpj, formatPhone } from "../../lib/masks";

export default function AdminAssociados() {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("Todos");
  const [exporting, setExporting] = useState(false);

  const load = () =>
    api
      .get("/admin/associados")
      .then((r) => setList(r.data))
      .catch(() => toast.error("Erro ao carregar associados."));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (u, status) => {
    if (status === "inativo" && !window.confirm(`Desativar ${u.name}?`)) return;
    try {
      await api.patch(`/admin/associados/${u.id}`, { status });
      toast.success("Status atualizado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/associados/export", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `ablemp-associados-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Planilha gerada");
    } catch (e) {
      toast.error("Não foi possível exportar a planilha.");
    } finally {
      setExporting(false);
    }
  };

  const filtered = list?.filter((u) =>
    filter === "Todos" ? true : u.status === filter.toLowerCase()
  );

  return (
    <div data-testid="admin-associados">
      <PageHeader
        title="Associados"
        subtitle="Aprove cadastros pendentes, edite perfis, gerencie status e exporte a base."
        actions={
          <div className="flex gap-2">
            <Link
              data-testid="novo-associado-btn"
              to="/admin/associados/novo"
              className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
            >
              <UserPlus size={14} /> Novo associado
            </Link>
            <Link
              data-testid="importar-associados-btn"
              to="/admin/associados/importar"
              className="inline-flex items-center gap-2 bg-[#002776] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#0A0F14]"
            >
              <Upload size={14} /> Importar planilha
            </Link>
            <button
              data-testid="export-xlsx"
              onClick={exportXlsx}
              disabled={exporting || !list || list.length === 0}
              className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776] disabled:opacity-60"
            >
              <FileDown size={14} />
              {exporting ? "Gerando..." : "Exportar Excel"}
            </button>
          </div>
        }
      />
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {["Todos", "Pendente", "Ativo", "Inativo"].map((s) => (
          <button
            key={s}
            data-testid={`assoc-filter-${s.toLowerCase()}`}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] border rounded-sm ${
              filter === s
                ? "bg-[#0A0F14] text-white border-[#0A0F14]"
                : "bg-white text-slate-700 border-slate-300 hover:border-[#002776]"
            }`}
          >
            {s}
          </button>
        ))}
        {list && (
          <span className="ml-auto text-[11px] uppercase tracking-[0.22em] text-slate-500">
            {filtered.length} {filtered.length === 1 ? "associado" : "associados"}
          </span>
        )}
      </div>

      {list === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="Nenhum associado nesta lista" />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left p-3">Nome do Associado</th>
                  <th className="text-left p-3">Contato</th>
                  <th className="text-left p-3">E-mail</th>
                  <th className="text-left p-3">Tipo do Associado</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Cadastro</th>
                  <th className="text-right p-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr
                    key={u.id}
                    data-testid={`assoc-row-${u.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="p-3">
                      <Link
                        to={`/admin/associados/${u.id}`}
                        className="font-medium hover:text-[#002776]"
                      >
                        {u.name}
                      </Link>
                      {u.cpf_cnpj && (
                        <div className="text-xs text-slate-500">
                          {formatCpfCnpj(u.cpf_cnpj, u.person_type)}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">
                      {u.phone ? (
                        formatPhone(u.phone)
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">{u.email}</td>
                    <td className="p-3 text-slate-600">
                      {u.tipo ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-[#002776]/30 bg-[#002776]/8 text-[#002776] text-[11px]">
                          {u.tipo}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <StatusBadge status={u.status} />
                    </td>
                    <td className="p-3 text-slate-600">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <Link
                          to={`/admin/associados/${u.id}`}
                          data-testid={`view-${u.id}`}
                          className="px-2 py-1 border border-slate-300 text-[10px] uppercase tracking-wide hover:border-[#002776]"
                          title="Ver perfil"
                        >
                          <Eye size={12} />
                        </Link>
                        {u.status === "pendente" && (
                          <>
                            <button
                              data-testid={`approve-${u.id}`}
                              onClick={() => setStatus(u, "ativo")}
                              className="inline-flex items-center gap-1 px-2 py-1 border border-emerald-300 text-emerald-700 text-[10px] uppercase tracking-wide hover:bg-emerald-50"
                            >
                              <Check size={12} /> Aprovar
                            </button>
                            <button
                              data-testid={`reject-${u.id}`}
                              onClick={() => setStatus(u, "inativo")}
                              className="inline-flex items-center gap-1 px-2 py-1 border border-rose-300 text-rose-700 text-[10px] uppercase tracking-wide hover:bg-rose-50"
                            >
                              <X size={12} /> Recusar
                            </button>
                          </>
                        )}
                        {u.status === "ativo" && (
                          <button
                            data-testid={`deactivate-${u.id}`}
                            onClick={() => setStatus(u, "inativo")}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-rose-300 text-rose-700 text-[10px] uppercase tracking-wide hover:bg-rose-50"
                          >
                            Desativar
                          </button>
                        )}
                        {u.status === "inativo" && (
                          <button
                            data-testid={`reactivate-${u.id}`}
                            onClick={() => setStatus(u, "ativo")}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-emerald-300 text-emerald-700 text-[10px] uppercase tracking-wide hover:bg-emerald-50"
                          >
                            <RotateCcw size={12} /> Reativar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
